import type {
  ActivitiesRow,
  UsersRow
} from "@outschool/db-queries/dist/generated/types";

import * as Auth from "@outschool/auth-shared";
import { refund_policy } from "@outschool/db-queries/dist/generated/types";
import {
  Activity,
  ClassFormat,
  DeliveryType,
  User as UserType
} from "@outschool/gql-backend-generated";
import { participationGuidelinesUrl } from "@outschool/routes";
import { plural } from "@outschool/text";
import _ from "lodash";

import * as RefundPolicies from "./RefundPolicies";
import * as User from "./User";

export const ACTIVITY_FORMAT_DESCRIPTION_FLEX_HIDDEN =
  "activityFormatDescriptionFlexHidden";
export const ACTIVITY_FORMAT_DESCRIPTION_ONGOING_HIDDEN =
  "activityFormatDescriptionOngoingHidden";
export const ACTIVITY_FORMAT_DESCRIPTION_ONE_TIME_HIDDEN =
  "activityFormatDescriptionOneTimeHidden";
export const ACTIVITY_FORMAT_DESCRIPTION_MULTI_DAY_HIDDEN =
  "activityFormatDescriptionMultiDayHidden";

export enum DeliveryTypeValues {
  OneOnOneClass = "One-on-one class",
  OneTimeClass = "One-time class",
  OngoingClass = "Ongoing class",
  SemesterCourse = "Semester course",
  ShortCourse = "Short course",
  Camp = "Camp",
  Club = "Group"
}

export enum FormatValues {
  Live = "Live online",
  Flex = "Flexible schedule"
}

export const Themes = [
  "Arts",
  "Coding & Tech",
  "English",
  "Health & Wellness",
  "Life Skills",
  "Math",
  "Music",
  "Science & Nature",
  "Social Studies",
  "World Languages",
  "Games & Hobbies"
] as const;
export type ThemeName = typeof Themes[number];
export function isThemeName(name: string): name is ThemeName {
  return Themes.includes(name as any);
}

/**
 * Primarily used on the homepage in "Classes by subject"
 */
export const SubjectThemes = [...Themes, "Learner Favorites"] as const;
export type SubjectThemeName = typeof SubjectThemes[number];
export function isSubjectThemeName(name: string): name is SubjectThemeName {
  return SubjectThemes.includes(name as any);
}

export const SubtopicsForThemes = {
  Arts: [
    "drawing",
    "photography",
    "dance",
    "theater",
    "film",
    "sewing"
  ] as const,
  "Coding & Tech": [
    "coding",
    "video game design",
    "robotics",
    "engineering",
    "electronics",
    "internet safety",
    "animation"
  ] as const,
  English: [
    "creative writing",
    "grammar & spelling",
    "book club",
    "essay writing",
    "poetry",
    "literature",
    "book clubs"
  ] as const,
  "Health & Wellness": [
    "hygiene",
    "managing emotions",
    "sex ed",
    "exercise"
  ] as const,
  "Life Skills": [
    "cooking & nutrition",
    "financial skills",
    "study skills",
    "social skills",
    "critical thinking"
  ] as const,
  Math: [
    "algebra",
    "arithmetic",
    "geometry",
    "fractions",
    "calculus",
    "statistics & probability"
  ] as const,
  Music: [
    "instrument lessons",
    "singing",
    "music composition",
    "music studies"
  ] as const,
  "Science & Nature": [
    "chemistry",
    "biology",
    "animal studies",
    "physics",
    "astronomy",
    "anatomy",
    "marine biology",
    "psychology"
  ] as const,
  "Social Studies": [
    "geography",
    "world history",
    "us history",
    "anthropology",
    "economics",
    "political science"
  ] as const,
  "World Languages": [
    "spanish",
    "sign language",
    "french",
    "japanese",
    "latin",
    "german",
    "chinese",
    "greek",
    "italian",
    "mandarin",
    "russian"
  ] as const,
  Interests: [
    "harry potter",
    "dungeons and dragons",
    "fortnite",
    "lego",
    "pokemon",
    "minecraft"
  ] as const
};
export type Subtopic =
  typeof SubtopicsForThemes[keyof typeof SubtopicsForThemes][number];

/**
 * Primarily used on the homepage in "Classes by subject"
 */
export const SubtopicsForSubjectThemes = {
  ...SubtopicsForThemes,
  "Learner Favorites": [
    "adhd",
    "adventure",
    "american history",
    "camp",
    "cats",
    "composers",
    "cooking",
    "dog training",
    "dogs",
    "emotions",
    "french conversation",
    "geology",
    "grammar",
    "guitar",
    "mindfulness",
    "music theory",
    "numbers",
    "nutrition",
    "piano",
    "politics",
    "probability",
    "science experiments",
    "spelling",
    "statistics",
    "travel",
    "ukulele",
    "unicorns",
    "weather",
    "zoology"
  ] as const
};
export type SubjectSubtopic =
  typeof SubtopicsForSubjectThemes[keyof typeof SubtopicsForSubjectThemes][number];

/**
 * @deprecated use: `const { Format } = useActivityString()`
 */
export const Format = [
  {
    value: "Live online",
    label: "Live online meetings"
  },
  "Flexible schedule"
];

export const ACTIVITY_DELIVERY: Readonly<Record<DeliveryType, string>> =
  Object.freeze({
    [DeliveryType.OneOnOneClass]: "One-on-one class", // deprecated - use capacityMax instead
    [DeliveryType.OneTimeClass]: "One-time class",
    [DeliveryType.OngoingClass]: "Ongoing class",
    [DeliveryType.SemesterCourse]: "Semester course",
    [DeliveryType.ShortCourse]: "Short course",
    [DeliveryType.Camp]: "Camp",
    [DeliveryType.Club]: "Group"
  });

/**
 * @deprecated use: `const { Delivery } = useActivityString()`
 */
export const Delivery = Object.values(ACTIVITY_DELIVERY);

export const classAgeMin = 3;
export const clubAgeMin = 8;
export const ageMax = 18;
export const ageOptions = _.range(classAgeMin, ageMax + 1);
export const clubAgeOptions = _.range(clubAgeMin, ageMax + 1);
/**
 * @deprecated use: `const { languageOptions } = useActivityString()`
 */
export const languageOptions = ["English", "Spanish"];

/**
 * @deprecated use: `const { strictAgeRangeOptions } = useActivityString()`
 */
export const strictAgeRangeOptions = [
  { value: "FLEXIBLE", label: "Flexible Age Range" },
  { value: "STRICT", label: "Strict Age Range" }
];
export const strictAgeRangeMaxOffset = 4;

export const durationWeeksOptions = _.range(1, 40 + 1);
export const flexDurationWeeksOptions = _.range(4, 20 + 1);
export const titleLengthMin = 10;
export const classTitleLengthMax = 80;
export const clubTitleLengthMax = 60;
export const summaryLengthMin = 40;
export const summaryLengthMax = 240;
export const classExperienceLengthMin = 500;
export const classAgeRangeMin = 5;
export const clubAgeRangeMin = 5;

export const sizeMin = 1;
export const sizeMax = 18;
export const sizeOptions = _.range(sizeMin, sizeMax + 1);

export const weeklyMeetingsOptions = _.range(1, 20 + 1);

export const minuteOptions = _.range(0, 55 + 5, 5);

export const hourOptions = _.range(0, 9 + 1, 1);

export const asyncTimeOptions = ["0", "0 - 1", "1 - 2", "2 - 4", "4+"];

export const totalClassesString = "140,000";

const minSpaces = 0;
const maxSpaces = 18;

export const sizeMaxOptions = (activity: { size_max: number }) =>
  _.range(minSpaces, offeredSpaces(activity) + 1);

export const offeredSpaces = (activity: { size_max: number }) =>
  Math.min(activity.size_max + 1, maxSpaces);

export const isArchived = (
  activity: Pick<ActivitiesRow, "archived_at"> | Pick<Activity, "archived_at">
) => Boolean(activity.archived_at);

export const isPublished = (
  activity: Pick<ActivitiesRow, "published_at"> | Pick<Activity, "published_at">
) => Boolean(activity?.published_at);

export const isRejected = (activity: Pick<Activity, "approvalStatus">) =>
  activity.approvalStatus && activity.approvalStatus.action === "reject";

export const isOnline = (
  activity: Pick<ActivitiesRow, "is_online"> | Pick<Activity, "is_online">
) => Boolean(activity.is_online);

export const isFlexSchedule = (
  activity:
    | Pick<ActivitiesRow, "weekly_meetings" | "duration_weeks">
    | Pick<Activity, "weekly_meetings" | "duration_weeks">
) => activity?.weekly_meetings === 0 && !!activity?.duration_weeks;

export const isOneOnOne = (activity: { size_max: number | null }) =>
  activity.size_max === 1;

export const isOneTime = (
  activity:
    | Pick<
        ActivitiesRow,
        "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly"
      >
    | Pick<Activity, "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly">
) =>
  !activity.is_ongoing_weekly &&
  activity.weekly_meetings === 1 &&
  activity.duration_weeks === 1;

export const isSeries = (
  activity:
    | Pick<
        ActivitiesRow,
        "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly" | "is_club"
      >
    | Pick<
        Activity,
        "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly" | "isClub"
      >
) => !isFlexSchedule(activity) && !isClub(activity) && !isOneTime(activity);

export const isCamp = (activity: {
  weekly_meetings: number;
  duration_weeks: number;
  title: string;
}) =>
  (activity.weekly_meetings >= 2 && activity.title.includes("camp")) ||
  (activity.duration_weeks <= 7 && activity.weekly_meetings > 1);

export function shouldAlwaysEnableZoom(
  activity:
    | Pick<ActivitiesRow, "weekly_meetings">
    | Pick<Activity, "weekly_meetings">
) {
  return !!activity?.weekly_meetings;
}

export const ACTIVITY_STATUS = Object.freeze({
  LISTED: "Listed",
  ARCHIVED: "Archived",
  NOT_LISTED: "Not listed",
  REQUESTED_LISTING: "Requested listing"
} as const);

/**
 * @deprecated use: `const { status } = useActivityString()`
 */
export const status = (
  activity: Pick<
    Activity,
    "archived_at" | "published_at" | "requested_listing_at"
  > & {
    leaderDetails?: {
      requestedListingAt?: Date;
    };
    requested_listing_at?: Date;
  }
) => {
  if (isArchived(activity)) {
    return ACTIVITY_STATUS.ARCHIVED;
  } else if (isPublished(activity)) {
    return ACTIVITY_STATUS.LISTED;
  } else if (requestedListingAt(activity)) {
    return ACTIVITY_STATUS.REQUESTED_LISTING;
  } else {
    return ACTIVITY_STATUS.NOT_LISTED;
  }
};

export enum ACTIVITY_FORMAT {
  FLEX = "Flexible Schedule",
  ONGOING = "Ongoing",
  ONE_TIME = "One-Time",
  MULTI_DAY = "Multi-Day",
  CLUB = "Group",
  ONE_ON_ONE = "1:1"
}

export type ActivityFormatType =
  | Pick<
      ActivitiesRow,
      "weekly_meetings" | "is_ongoing_weekly" | "duration_weeks"
    >
  | Pick<
      Activity,
      | "weekly_meetings"
      | "duration_weeks"
      | "is_ongoing_weekly"
      | "isClub"
      | "hasTeacherSchedule"
    >;

export const format = (activity: ActivityFormatType) => {
  if (activity["isClub"] || activity["is_club"]) {
    return ACTIVITY_FORMAT.CLUB;
  } else if (activity["hasTeacherSchedule"]) {
    return ACTIVITY_FORMAT.ONE_ON_ONE;
  } else if (isFlexSchedule(activity)) {
    return ACTIVITY_FORMAT.FLEX;
  } else if (!!activity.is_ongoing_weekly) {
    return ACTIVITY_FORMAT.ONGOING;
  } else if (isOneTime(activity)) {
    return ACTIVITY_FORMAT.ONE_TIME;
  } else {
    return ACTIVITY_FORMAT.MULTI_DAY;
  }
};

export function formatToUpdates(
  format: ACTIVITY_FORMAT,
  currentActivity: { weekly_meetings: number; duration_weeks: number }
) {
  return {
    [ACTIVITY_FORMAT.FLEX]: {
      is_ongoing_weekly: false,
      weekly_meetings: 0,
      duration_weeks:
        currentActivity.duration_weeks >= 4
          ? currentActivity.duration_weeks
          : 4,
      refund_policy_type: RefundPolicies.REFUND_POLICY_TYPES.FLEXIBLE,
      allows_recurring_payment: true
    },
    [ACTIVITY_FORMAT.ONGOING]: {
      is_ongoing_weekly: true,
      weekly_meetings: currentActivity.weekly_meetings || 1,
      refund_policy_type: RefundPolicies.REFUND_POLICY_TYPES.LIMITED,
      allows_recurring_payment: true
    },
    [ACTIVITY_FORMAT.ONE_TIME]: {
      is_ongoing_weekly: false,
      weekly_meetings: 1,
      duration_weeks: 1,
      refund_policy_type: RefundPolicies.REFUND_POLICY_TYPES.LIMITED,
      allows_recurring_payment: false
    },
    [ACTIVITY_FORMAT.MULTI_DAY]: {
      is_ongoing_weekly: false,
      weekly_meetings: currentActivity.weekly_meetings || 1,
      duration_weeks:
        currentActivity.duration_weeks > 1 ? currentActivity.duration_weeks : 2,
      refund_policy_type: RefundPolicies.REFUND_POLICY_TYPES.FLEXIBLE,
      allows_recurring_payment: true
    }
  }[format];
}

export const shortFormat = (activity: ActivityFormatType) => {
  return {
    [ACTIVITY_FORMAT.FLEX]: "flex",
    [ACTIVITY_FORMAT.ONGOING]: "ongoing",
    [ACTIVITY_FORMAT.ONE_TIME]: "one-time",
    [ACTIVITY_FORMAT.MULTI_DAY]: "multi-day",
    [ACTIVITY_FORMAT.ONE_ON_ONE]: "1:1",
    [ACTIVITY_FORMAT.CLUB]: "group"
  }[format(activity)];
};

export const gqlFormat = (format: ACTIVITY_FORMAT) => {
  return {
    [ACTIVITY_FORMAT.FLEX]: ClassFormat.Flex,
    [ACTIVITY_FORMAT.ONGOING]: ClassFormat.Ongoing,
    [ACTIVITY_FORMAT.ONE_TIME]: ClassFormat.OneTime,
    [ACTIVITY_FORMAT.MULTI_DAY]: ClassFormat.MultiDay
  }[format];
};

export const formatString = (activity: ActivityFormatType) => {
  if (activity["hasTeacherSchedule"]) {
    return "1:1 Class";
  }

  if (activity["isClub"] || activity["is_club"]) {
    return "Group";
  }

  const formatClassOrCourse =
    format(activity) === ACTIVITY_FORMAT.ONE_TIME ? "Class" : "Course";
  return `${format(activity)} ${formatClassOrCourse}`;
};

/**
 * @deprecated use: `const { shortFormatDescriptions } = useActivityString()`
 */
export const shortFormatDescriptions = {
  [ACTIVITY_FORMAT.FLEX]: `These classes occur using asynchronous messaging on the Outschool platform, and do not meet live over video.`,
  [ACTIVITY_FORMAT.ONGOING]: `Learners subscribe to these classes, which meet live over video on a weekly schedule. Learners can join or leave at any time.`,
  [ACTIVITY_FORMAT.ONE_TIME]: `These classes meet live over video a single time. Discussion may occur by messaging on the classroom page before or after class.`,
  [ACTIVITY_FORMAT.MULTI_DAY]: `These courses meet live over video over multiple days. Discussion occurs by messaging on the classroom page between meetings.`
};

/**
 * @deprecated use: `const { shortFormatDescription } = useActivityString()`
 */
export const shortFormatDescription = (activity: ActivityFormatType) => {
  return shortFormatDescriptions[format(activity)];
};

export function getFormatDescriptionPreferenceKey(
  activity: ActivityFormatType
) {
  return {
    [ACTIVITY_FORMAT.FLEX]: ACTIVITY_FORMAT_DESCRIPTION_FLEX_HIDDEN,
    [ACTIVITY_FORMAT.ONGOING]: ACTIVITY_FORMAT_DESCRIPTION_ONGOING_HIDDEN,
    [ACTIVITY_FORMAT.ONE_TIME]: ACTIVITY_FORMAT_DESCRIPTION_ONE_TIME_HIDDEN,
    [ACTIVITY_FORMAT.MULTI_DAY]: ACTIVITY_FORMAT_DESCRIPTION_MULTI_DAY_HIDDEN
  }[format(activity)];
}

/**
 * @deprecated use: `const { frequencyLanguage } = useActivityString()`
 */
export const frequencyLanguage = (numMeetings?: number) => {
  if (numMeetings === undefined) {
    return "";
  }
  if (numMeetings === 1) {
    return "Once per week";
  } else if (numMeetings === 2) {
    return "Twice per week";
  } else {
    return `${numMeetings}x per week`;
  }
};

const asyncTimeString = (
  activity: Pick<ActivitiesRow, "details"> | Pick<Activity, "details">
) => {
  if (
    activity.details.asyncTime === undefined ||
    activity.details.asyncTime === null
  ) {
    return "maybe some time outside of class";
  } else if (
    activity.details.asyncTime === "0" ||
    activity.details.asyncTime === 0
  ) {
    return "no time outside of class";
  } else {
    return (
      "an estimated " +
      activity.details.asyncTime +
      " hours per week outside of class"
    );
  }
};

const liveTimeString = (activity: {
  weekly_meetings: number;
  duration_minutes: number;
}) => {
  if (activity.weekly_meetings === 0) {
    return "No live meetings";
  }

  if (activity.weekly_meetings === null || activity.duration_minutes === null) {
    // should only happen for draft classes
    return "UNSPECIFIED time per week in class";
  }

  const totalMinutes = activity.duration_minutes * activity.weekly_meetings;
  const hours = Math.floor(totalMinutes / 60.0);
  const extraMinutes = totalMinutes - hours * 60;
  let result;
  if (hours) {
    result = `${hours} ${plural("hour", hours)}`;
    if (extraMinutes) {
      result += ` ${extraMinutes} minutes`;
    }
  } else {
    result = `${extraMinutes} minutes`;
  }

  return `${result} per week in class`;
};

/**
 * @deprecated use: `const { learnerTimeString } = useActivityString()`
 */
export const learnerTimeString = (
  activity: {
    weekly_meetings: number;
    duration_minutes: number;
  } & Pick<Activity, "details" | "isClub">
) => {
  if (activity.isClub) {
    return "View and create posts any time, at your own pace.";
  }
  return liveTimeString(activity) + ", and " + asyncTimeString(activity);
};

/**
 * @deprecated use: `const { durationString } = useActivityString()`
 */
export const durationString = (
  activity:
    | Pick<
        ActivitiesRow,
        "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly" | "is_club"
      >
    | Pick<
        Activity,
        "weekly_meetings" | "duration_weeks" | "is_ongoing_weekly" | "isClub"
      >
) => {
  if (isClub(activity)) {
    return "Group";
  }

  if (activity.weekly_meetings === null || activity.duration_weeks === null) {
    return "Unknown schedule";
  }

  if (!!activity.is_ongoing_weekly) {
    return `Meets ${frequencyLanguage(activity.weekly_meetings)}`;
  } else if (isFlexSchedule(activity)) {
    return `Flexible for ${activity.duration_weeks} ${plural(
      "week",
      activity.duration_weeks
    )}`;
  } else if (isOneTime(activity)) {
    return "Meets Once";
  } else {
    return `${activity.duration_weeks} ${plural(
      "Week",
      activity.duration_weeks
    )}, ${frequencyLanguage(activity.weekly_meetings)}`;
  }
};

/**
 * class size is determined at the activity level
 * Some sections may override size_min, size_max
 *
 * @deprecated use: `const { classSizeString } = useActivityString()`
 */

export function classSizeString(
  activity:
    | Pick<ActivitiesRow, "size_min" | "size_max">
    | Pick<Activity, "size_min" | "size_max">
) {
  const minSize = activity.size_min;
  const maxSize = activity.size_max;

  if (!minSize && !maxSize) {
    return "TBD";
  }

  let result = minSize || "1";
  if (maxSize) {
    if (maxSize === 1) {
      return "1";
    } else if (minSize === undefined || minSize === null || maxSize > minSize) {
      result += "-" + maxSize;
    }
  } else {
    result += " or more";
  }

  return result;
}

export function createClassDraft(
  title: string,
  email: string,
  age_min?: number,
  age_max?: number
) {
  return {
    title: title,
    details: {
      email: email
    },
    checklist: {},
    location: {},
    age_min,
    age_max,
    size_min: 3,
    size_max: 6,
    price_cents: 1000,
    duration_minutes: 55,
    duration_weeks: 1,
    weekly_meetings: 1,
    is_online: true,
    refund_policy_type: refund_policy.limited,
    is_club: false,
    allows_recurring_payment: false,
    allows_late_enrollment: false,
    published_at: null,
    first_listed_at: null,
    archived_at: null
  };
}

export function createClubDraft(title: string, email: string) {
  return {
    title: title,
    details: {
      email: email
    },
    checklist: {},
    location: {},
    size_min: 0,
    // TODO: Allow users to enroll to clubs when size is null
    size_max: 100000000,
    price_cents: 500,
    is_online: true,
    refund_policy_type: refund_policy.limited,
    is_club: true,
    is_auto_scheduling_enabled: false,
    allows_recurring_payment: true,
    allows_late_enrollment: true
  };
}

export const isOwner = (
  activity: Pick<ActivitiesRow, "user_uid"> | Pick<Activity, "user_uid">,
  user: Pick<UsersRow, "uid">
) => Boolean(user) && activity.user_uid === user.uid;

// Use Section.canManage if you are checking seller org teachers as well
export const canManage = (
  activity: Pick<ActivitiesRow, "user_uid"> | Pick<Activity, "user_uid">,
  userOrAuthToken:
    | Pick<UsersRow, "uid" | "is_admin">
    | Pick<UserType, "uid" | "is_admin">
) =>
  Boolean(userOrAuthToken) &&
  (User.isAdmin(userOrAuthToken) || isOwner(activity, userOrAuthToken));

export const canApprove = (userOrAuthToken: Auth.Auth) =>
  Auth.hasRole(userOrAuthToken, Auth.Roles.ClassApproval);

export const canView = (
  activity:
    | Pick<ActivitiesRow, "user_uid" | "published_at">
    | Pick<Activity, "user_uid" | "published_at">,
  user: Auth.Auth
) =>
  Boolean(activity?.published_at) ||
  isOwner(activity, user) ||
  User.isAdmin(user) ||
  Auth.hasRole(user, Auth.Roles.ClassApproval);

export const hasLocation = (
  activity: Pick<ActivitiesRow, "location"> | Pick<Activity, "location">
) =>
  Boolean(activity.location) &&
  Boolean(activity.location.city) &&
  activity.location.lat !== undefined &&
  activity.location.lng !== undefined;

export const isSemesterCourse = (activity: { duration_weeks: number }) =>
  activity.duration_weeks >= 8;

export const isLive = (activity: { weekly_meetings: number }) => {
  return !!activity?.weekly_meetings;
};

export const isShortCourse = (activity: { duration_weeks: number }) =>
  2 <= activity.duration_weeks && activity.duration_weeks <= 7;

export const isLongCourse = (activity: { duration_weeks: number }) =>
  activity.duration_weeks >= 8;

export const isAppropriateForAge = (
  activity:
    | Pick<ActivitiesRow, "age_min" | "age_max">
    | Pick<Activity, "age_min" | "age_max">,
  age: number
) =>
  (activity.age_min === null ||
    activity.age_min === undefined ||
    age >= activity.age_min) &&
  (activity.age_max === null ||
    activity.age_max === undefined ||
    age <= activity.age_max);

export const isAppropriateForAges = (
  activity:
    | Pick<ActivitiesRow, "age_min" | "age_max">
    | Pick<Activity, "age_min" | "age_max">,
  ages: number[] | string
) => {
  if (typeof ages === "string") {
    ages = ages.split(",").map(e => Number.parseInt(e, 10));
  }

  if (ages && ages.length) {
    return ages.reduce(function (prev, curr) {
      return prev || isAppropriateForAge(activity, curr);
    }, false);
  } else {
    return true;
  }
};

export const isFree = (
  activity: Pick<ActivitiesRow, "price_cents"> | Pick<Activity, "price_cents">
) => activity.price_cents === 0;

export function getDeliveryType(activity: {
  duration_weeks: number;
  weekly_meetings: number;
  title: string;
  is_ongoing_weekly: boolean;
  size_max: number;
}) {
  const values: DeliveryType[] = [];

  if (isCamp(activity)) {
    values.push(DeliveryType.Camp);
  }

  if (
    typeof activity.duration_weeks === "number" &&
    isSemesterCourse({ duration_weeks: activity.duration_weeks })
  ) {
    values.push(DeliveryType.SemesterCourse);
  }

  if (isOneTime(activity)) {
    values.push(DeliveryType.OneTimeClass);
  }

  if (!!activity.is_ongoing_weekly) {
    values.push(DeliveryType.OngoingClass);
  }

  if (
    typeof activity.duration_weeks === "number" &&
    isShortCourse({ duration_weeks: activity.duration_weeks })
  ) {
    values.push(DeliveryType.ShortCourse);
  }

  if (isOneOnOne(activity)) {
    values.push(DeliveryType.OneOnOneClass);
  }

  return values;
}

/**
 *
 * @deprecated use: `const { ageRangeString } = useActivityString()`
 */
export function ageRangeString(
  activity:
    | Pick<ActivitiesRow, "age_min" | "age_max">
    | Pick<Activity, "age_min" | "age_max">,
  useAgesPrefix = true
) {
  const minAge = activity.age_min;
  const maxAge = activity.age_max;
  if (!minAge && !maxAge) {
    return "All ages";
  }

  const prefix = useAgesPrefix ? "Ages " : "";

  if (!minAge) {
    return prefix + maxAge + " or younger";
  }

  if (!maxAge) {
    return prefix + minAge + " and up";
  }

  return prefix + minAge + "-" + maxAge;
}

/**
 *
 * @deprecated use: `const { strictAgeRangeString } = useActivityString()`
 */
export function strictAgeRangeString(
  activity: Pick<Activity, "age_min" | "age_max" | "strictAgeRange">
) {
  const { age_min, age_max, strictAgeRange } = activity;
  if (
    age_min === null ||
    age_min === undefined ||
    age_max === null ||
    age_max === undefined ||
    strictAgeRange === null ||
    strictAgeRange === undefined
  ) {
    return "";
  }
  const minAge = age_min - strictAgeRange.min;
  const maxAge = age_max + strictAgeRange.max;

  if (!minAge) {
    return maxAge + " or younger";
  }

  if (!maxAge) {
    return minAge + " and up";
  }

  return minAge + "-" + maxAge;
}

type AgeType = {
  age_min?: number | null;
  age_max?: number | null;
};
type DBAgeType = AgeType &
  Pick<ActivitiesRow, "strict_age_min" | "strict_age_max">;
type GQLAgeType = AgeType & Pick<Activity, "strictAgeRange">;

function isGQLAgeType(ageType: DBAgeType | GQLAgeType): ageType is GQLAgeType {
  return ageType.hasOwnProperty("strictAgeRange");
}

/**
 * @param {ActivitiesRow|Activity} activity The activity to check for.
 * @param {number} learnerAge - The learner's age.
 * @param {boolean} sectionIsPublished - The learner's age.
 */
export const learnerOutsideStrictAgeRange = (
  activity: DBAgeType | GQLAgeType,
  ignoreStrictAgeRange: boolean,
  learnerAge: number
) => {
  const { age_min, age_max } = activity;
  if (
    age_min === null ||
    age_min === undefined ||
    age_max === null ||
    age_max === undefined
  ) {
    return false;
  }
  //Strict age ranges don't apply to private sections
  const strictAgeMin = isGQLAgeType(activity)
    ? activity.strictAgeRange?.min
    : activity.strict_age_min;
  const strictAgeMax = isGQLAgeType(activity)
    ? activity.strictAgeRange?.max
    : activity.strict_age_max;
  if (ignoreStrictAgeRange) {
    return false;
  }
  if (typeof strictAgeMin !== "number" || typeof strictAgeMax !== "number") {
    return false;
  }
  return (
    learnerAge < age_min - strictAgeMin || learnerAge > age_max + strictAgeMax
  );
};

export function requestedListingAt(activity: {
  leaderDetails?: {
    requestedListingAt?: Date;
  };
  requested_listing_at?: Date;
}) {
  return activity.leaderDetails
    ? activity.leaderDetails.requestedListingAt
    : activity.requested_listing_at;
}

export const MIN_PRICE = `$10 USD`;

/**
 * @deprecated use: `const { WELCOME_MESSAGE } = useActivityString()`
 */
export const WELCOME_MESSAGE = `Welcome! I'm excited that you joined this class. Would you please introduce yourself to me and the other learners? Remember to keep your private information private. For example, no last names, home cities, or gaming handles! I'd also love for you to share any background or questions you have in this topic area.

As a reminder, when posting in the classroom remember to follow the Outschool guidelines to be kind, safe, and respectful: ${participationGuidelinesUrl()}`;

// Used on the activity detail page and in ads
export const MAIN_IMAGE_WIDTH = 344;
export const MAIN_IMAGE_WIDTH_1_9_ASPECT_RATIO = 344;
export const MAIN_IMAGE_HEIGHT = 179;

export const SEASONAL_EMAIL_ACTIVITY_START_DATE = "2020-06-25";
export const SEASONAL_EMAIL_ACTIVITY_END_DATE = "2020-07-17";

export function isSoldOut(
  activity: Pick<
    Activity,
    "upcomingSectionCount" | "upcomingAvailableSectionCount"
  >
) {
  return (
    activity.upcomingSectionCount > 0 &&
    activity.upcomingAvailableSectionCount == 0
  );
}

/**
 * @deprecated use: the `isClub` field from the GQL type directly if on client, `is_club` column if on server
 */
export const isClub = (
  activity: Partial<Pick<Activity, "isClub"> & Pick<ActivitiesRow, "is_club">>
) => Boolean(activity.is_club) || Boolean(activity.isClub);

export const getMaxStrictAgeMin = (
  currentAgeRangeMin: number,
  ageMin: number
) =>
  !currentAgeRangeMin
    ? strictAgeRangeMaxOffset
    : Math.min(currentAgeRangeMin - ageMin, strictAgeRangeMaxOffset);

export const getMaxStrictAgeMax = (currentAgeRangeMax: number) =>
  !currentAgeRangeMax
    ? strictAgeRangeMaxOffset
    : Math.min(ageMax - currentAgeRangeMax, strictAgeRangeMaxOffset);

/**
 * @deprecated use: `const { DEFAULT_CLUB_GUIDELINES } = useActivityString()`
 */
export const DEFAULT_CLUB_GUIDELINES = `Welcome to our Group! We want all of our learners to feel comfortable, connected, and safe. That's why we have 3 rules we ask you to follow: Be Kind, Be Safe, and Be Respectful.

Be Kind - Help all learners feel welcome and included.
Learners from all over the world, with different backgrounds, races, ethnicities, abilities, gender identities, religions and body types, participate in our Group. Always be nice and make others, especially new learners, know that they belong and are welcome. Teasing, name calling, or bullying anybody in our community? Nope. Not happening here

Be Safe - Keep your personal information private and never ask other learners for theirs.
Personal things are personal. We want to keep you safe on Outschool, but we also want YOU to understand how to be safe when you're meeting other learners or teachers. Keep your full name, home address, gaming or social media name, email, and school name private.

Be Respectful - Treat others how you want to be treated.
Keep language clean and information you share safe. If you wouldn't say it or share with your grandma, don't do it here. Swear words, racy videos, and violent images are not allowed. Treat Outschool like a public place, and when in doubt, keep things covered.`;

export const LEARNER_COMMUNITY_CHAT_ACTIVITY_UID =
  "53f97d44-7bef-4082-8f43-145bac359865";

export const DEFAULT_ONGOING_DURATION_WEEK = 4;
export const AUTO_SCHEDULING_DAYS_NOTICE_MIN_DEFAULT = 14;
export const AUTO_SCHEDULING_DAYS_NOTICE_MAX_DEFAULT = 90;
export const AUTO_SCHEDULE_DAYS_NOTICE_MIN_MINIMUM_VALUE = 1;
export const AUTO_SCHEDULE_DAYS_NOTICE_MIN_MAXIMUM_VALUE = 21;

export const ACTIVITY_ENROLL_CLICK = "Activity Enroll Click";
